import { useState, useEffect } from "react";
import { Download, X, Calendar, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API, getToken } from "../lib/api";
import { api } from "../lib/api";

/**
 * CsvExportDialog — opens a modal that lets the therapist pick an optional
 * date range, previews row-count, then downloads the CSV.
 *
 * Variants:
 *   variant="client"  — scoped to a single client (uses /clients/{id}/export.csv)
 *   variant="batch"   — date-range across all clients (uses /exports/sessions.csv)
 *
 * The CSV endpoint accepts the JWT either as a Bearer header or as an `auth=`
 * query parameter so a plain <a download> link works for direct downloads.
 */
export default function CsvExportDialog({ open, onClose, variant = "client", clientId, clientName }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [preview, setPreview] = useState(null);

  // Close on Escape for keyboard a11y.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const params = new URLSearchParams();
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  if (variant === "batch" && clientId) params.set("client_id", clientId);
  const downloadPath = variant === "client"
    ? `/clients/${clientId}/export.csv`
    : `/exports/sessions.csv`;
  const previewPath = `/exports/sessions.preview`;

  const downloadUrl = `${API}${downloadPath}?${params.toString()}&auth=${getToken()}`;

  const runPreview = async () => {
    setLoadingPreview(true);
    setPreview(null);
    try {
      const previewParams = new URLSearchParams(params);
      // /exports/sessions.preview accepts client_id for both variants.
      if (variant === "client" && clientId) previewParams.set("client_id", clientId);
      const r = await api.get(`${previewPath}?${previewParams.toString()}`);
      setPreview(r.data);
    } catch {
      toast.error("Could not load preview");
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleDownload = () => {
    // <a download> works because the route honors ?auth= and sends
    // attachment headers — this avoids a blob fetch + revoke dance.
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.rel = "noopener";
    a.click();
    toast.success("Export started — your CSV will download shortly.");
  };

  const title = variant === "client"
    ? `Export sessions for ${clientName || "client"}`
    : "Batch export sessions";

  return (
    <div
      className="fixed inset-0 z-50 bg-ink-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="csv-export-dialog"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl w-full max-w-md shadow-2xl border border-paper-200 p-7"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="label-overline mb-1">CSV export</div>
            <h2 className="font-heading text-xl font-light text-ink-900">{title}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="csv-export-close"
            className="w-9 h-9 rounded-full hover:bg-paper-100 text-ink-500 flex items-center justify-center"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="text-sm text-ink-600 mb-5 leading-relaxed">
          Downloads one row per session. Includes pain deltas, ROM gains, muscles
          treated, AI summary, recommendations, and therapist notes.
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="label-overline mb-1.5 block">From</label>
            <div className="relative">
              <input
                type="date"
                value={from}
                onChange={(e) => { setFrom(e.target.value); setPreview(null); }}
                data-testid="csv-export-from"
                className="w-full min-h-[40px] px-3 pr-9 rounded-xl border border-paper-200 bg-paper-50 text-sm focus:outline-none focus:border-ink-400"
              />
              <Calendar className="w-4 h-4 text-ink-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          </div>
          <div>
            <label className="label-overline mb-1.5 block">To</label>
            <div className="relative">
              <input
                type="date"
                value={to}
                onChange={(e) => { setTo(e.target.value); setPreview(null); }}
                data-testid="csv-export-to"
                className="w-full min-h-[40px] px-3 pr-9 rounded-xl border border-paper-200 bg-paper-50 text-sm focus:outline-none focus:border-ink-400"
              />
              <Calendar className="w-4 h-4 text-ink-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          </div>
        </div>

        <div className="text-xs text-ink-500 mb-5">
          Leave dates blank to export every session{variant === "client" ? " for this client." : "."}
        </div>

        <div className="flex items-center gap-2 mb-5">
          <button
            type="button"
            onClick={runPreview}
            disabled={loadingPreview}
            data-testid="csv-export-preview-btn"
            className="min-h-[40px] px-4 rounded-full border border-paper-200 hover:bg-paper-100 text-sm text-ink-700 inline-flex items-center gap-2 disabled:opacity-50"
          >
            {loadingPreview ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            Preview count
          </button>
          {preview && (
            <div className="text-sm text-ink-700" data-testid="csv-export-row-count">
              <strong className="text-ink-900">{preview.row_count}</strong> row{preview.row_count === 1 ? "" : "s"} match
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="min-h-[44px] px-5 rounded-full border border-paper-200 hover:bg-paper-100 text-sm text-ink-700"
            data-testid="csv-export-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDownload}
            data-testid="csv-export-download-btn"
            className="min-h-[44px] px-5 rounded-full bg-ink-900 hover:bg-ink-700 text-white text-sm font-medium inline-flex items-center gap-2"
          >
            <Download className="w-4 h-4" /> Download CSV
          </button>
        </div>
      </div>
    </div>
  );
}
